import React, { useState, useEffect, useRef } from "react";
import { MessageSquare, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { OpvolgerAvatar } from "./Opvolger";
import { fmtReactieTijd, leesConcept, bewaarConcept, zoekEditConcept } from "./reactieUtils";

const MAX = 2000;
const NEAR = 1800;
const inflight = new Map();   // `${me}|${afspraakId}` -> { promise, sent }: een lopende post overleeft het wisselen van afspraak
const taCls = "w-full px-3 py-2 rounded-md border border-zinc-300 bg-white text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-600 resize-y min-h-[72px] read-only:bg-zinc-50 read-only:text-zinc-500";
const knop = "flex items-center gap-1 px-2 min-h-[44px] sm:min-h-[28px] rounded text-xs";
const foutTekst = (e, standaard) => (e?.code === "ECONNABORTED"
  ? "Geen antwoord van de server (time-out). Je tekst staat nog in het vak; kijk of de reactie al staat voordat je opnieuw probeert."
  : e?.response?.data?.detail || standaard);
const TIMEOUT = { timeout: 30000 };
const meervoud = (n) => `${n} ${n === 1 ? "reactie" : "reacties"}`;

export const ReactieTeller = ({ n, testid }) => (n > 0 ? (
  <span data-testid={testid} className="inline-flex items-center gap-1 font-mono text-xs text-zinc-500 shrink-0">
    <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" /><span aria-hidden="true">{n}</span>
    <span className="sr-only">{meervoud(n)}</span>
  </span>
) : null);

const BesluitChip = () => (
  <span data-testid="reactie-besluit-label" className="inline-flex text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">Besluit</span>
);

function Invoer({ waarde, onTekst, besluit, onBesluit, onSubmit, idp, label, placeholder, vast }) {
  const n = waarde.trim().length;
  return (
    <>
      <textarea data-testid={`${idp}-tekst`} aria-label={label} value={waarde} rows={3} placeholder={placeholder} readOnly={vast}
        onChange={(e) => onTekst(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") e.stopPropagation();
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); onSubmit(); }
        }} className={taCls} />
      <div className="flex items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer min-h-[44px] sm:min-h-0">
          <Checkbox data-testid={`${idp}-besluit`} checked={besluit} disabled={vast} onCheckedChange={(v) => onBesluit(v === true)} />
          Dit is een besluit
        </label>
        {n >= NEAR && <span data-testid={`${idp}-teken`} className={`font-mono text-xs ${n > MAX ? "text-rose-600 font-bold" : "text-amber-700"}`}>{n}/{MAX}</span>}
      </div>
      {n > MAX && <p data-testid={`${idp}-te-lang`} className="text-xs text-rose-600">Je reactie is {n - MAX} {n - MAX === 1 ? "teken" : "tekens"} te lang: de grens is {MAX} tekens. Kort hem in om te kunnen opslaan.</p>}
    </>
  );
}

function Regel({ r, me, onEdit, onWis, leesalleen }) {
  return (
    <li data-testid={`reactie-${r.id}`} className="flex gap-2.5">
      <OpvolgerAvatar name={r.auteur} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
          <span data-testid={`reactie-auteur-${r.id}`} className="font-semibold text-zinc-900 capitalize">{r.auteur}</span>
          <span data-testid={`reactie-tijd-${r.id}`} className="font-mono text-zinc-500">{fmtReactieTijd(r.createdAt)}</span>
          {r.editedAt && <span data-testid={`reactie-bewerkt-${r.id}`} className="text-zinc-400">bewerkt</span>}
          {r.besluit && <BesluitChip />}
          {r.auteur === me && !leesalleen && (
            <span className="ml-auto flex items-center gap-1">
              <button type="button" data-testid={`reactie-bewerken-${r.id}`} onClick={() => onEdit(r)} className={`${knop} text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100`}>
                <Pencil className="h-3 w-3" />Bewerken
              </button>
              <button type="button" data-testid={`reactie-verwijderen-${r.id}`} onClick={() => onWis(r)} className={`${knop} text-zinc-500 hover:text-rose-600 hover:bg-rose-50`}>
                <Trash2 className="h-3 w-3" />Verwijderen
              </button>
            </span>
          )}
        </div>
        <p data-testid={`reactie-tekst-${r.id}`} className="text-sm text-zinc-800 whitespace-pre-line break-words mt-0.5">{r.tekst}</p>
      </div>
    </li>
  );
}

export function Reacties({ a, me, onReacties, leesalleen = false }) {
  const lijst = a.reacties || [];
  const key = `${me}|${a.id}`;
  const [tekst, setTekstS] = useState(() => leesConcept(key)?.tekst || "");
  const [besluit, setBesluitS] = useState(() => leesConcept(key)?.besluit || false);
  const eprefix = `edit|${me}|${a.id}|`;
  const lopend = () => [...inflight.entries()].filter(([k]) => k === key || k.startsWith(eprefix)).map(([, f]) => f);
  const [bezig, setBezig] = useState(() => lopend().length > 0);
  const [edit, setEditS] = useState(() => zoekEditConcept(me, a.id));
  const [wis, setWis] = useState(null);
  const alive = useRef(true);
  const tekstRef = useRef(tekst);
  tekstRef.current = tekst;

  const volg = (f) => f.promise.then((ok) => {
    if (!alive.current) return;
    setBezig(false);
    if (ok && f.rid) setEditS(null);
    else if (ok) { setTekstS((cur) => (cur.trim() === f.sent ? "" : cur)); setBesluitS(false); }
  });
  useEffect(() => {
    alive.current = true;
    const fs = lopend();
    if (fs.length) fs.forEach(volg); else setBezig(false);
    return () => { alive.current = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const setTekst = (t) => { setTekstS(t); bewaarConcept(key, { tekst: t, besluit }); };
  const setBesluit = (b) => { setBesluitS(b); bewaarConcept(key, { tekst, besluit: b }); };
  const setEdit = (e) => {
    const vorig = edit;
    setEditS(e);
    if (vorig && (!e || e.id !== vorig.id)) bewaarConcept(`edit|${me}|${a.id}|${vorig.id}`, null);
    if (e) bewaarConcept(`edit|${me}|${a.id}|${e.id}`, { tekst: e.tekst, besluit: e.besluit });
  };
  const teLang = tekst.trim().length > MAX;
  const leeg = !tekst.trim() || teLang;

  const plaats = async () => {
    if (leeg || bezig || inflight.has(key)) return;
    const sent = tekst.trim();
    const b = besluit;
    const promise = api.post(`/afspraken/${a.id}/reacties`, { tekst: sent, besluit: b }, TIMEOUT)
      .then((r) => {
        onReacties(a.id, (l) => [...l, r.data]);
        if ((leesConcept(key)?.tekst || "").trim() === sent) bewaarConcept(key, null);
        return true;
      })
      .catch((e) => { toast.error(foutTekst(e, "Reactie plaatsen mislukt. Je tekst staat nog in het vak.")); return false; })
      .finally(() => inflight.delete(key));
    const f = { promise, sent };
    inflight.set(key, f);
    setBezig(true);
    volg(f);
  };
  const bewaarEdit = () => {
    if (!edit || bezig) return;
    const orig = lijst.find((x) => x.id === edit.id);
    const t = edit.tekst.trim();
    if (!t || t.length > MAX) return;
    if (orig && orig.tekst === t && !!orig.besluit === !!edit.besluit) { setEdit(null); return; }
    const ekey = `${eprefix}${edit.id}`;
    if (inflight.has(ekey)) return;
    const rid = edit.id;
    const besl = edit.besluit;
    const promise = api.patch(`/afspraken/${a.id}/reacties/${rid}`, { tekst: t, besluit: besl }, TIMEOUT)
      .then((r) => { onReacties(a.id, (l) => l.map((x) => (x.id === rid ? r.data : x))); bewaarConcept(ekey, null); return true; })
      .catch((e) => { toast.error(foutTekst(e, "Reactie bewerken mislukt. Je wijziging staat nog in het vak.")); return false; })
      .finally(() => inflight.delete(ekey));
    const f = { promise, rid };
    inflight.set(ekey, f);
    setBezig(true);
    volg(f);
  };
  const verwijder = async () => {
    const r = wis;
    setWis(null);
    try {
      await api.delete(`/afspraken/${a.id}/reacties/${r.id}`, TIMEOUT);
      onReacties(a.id, (l) => l.filter((x) => x.id !== r.id));
      toast.success("Reactie verwijderd");
    } catch (e) {
      toast.error(foutTekst(e, "Reactie verwijderen mislukt"));
    }
  };
  const editOngeldig = !edit || !edit.tekst.trim() || edit.tekst.trim().length > MAX;

  return (
    <div data-testid="reacties-blok" className="space-y-3 pt-4 border-t border-zinc-200">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-400">
        <MessageSquare className="h-3 w-3" /> Reacties
        <span data-testid="reacties-aantal" className="font-mono text-zinc-500">{lijst.length}</span>
      </div>
      {lijst.length === 0 ? <p data-testid="reacties-leeg" className="text-sm text-zinc-500">Nog geen reacties.</p> : (
        <ul data-testid="reacties-lijst" className="space-y-3">
          {lijst.map((r) => (edit?.id === r.id && !leesalleen ? (
            <li key={r.id} data-testid={`reactie-edit-${r.id}`} className="space-y-2">
              <Invoer idp="reactie-edit" label="Reactie bewerken" waarde={edit.tekst} onTekst={(t) => setEdit({ ...edit, tekst: t })} besluit={edit.besluit}
                onBesluit={(b) => setEdit({ ...edit, besluit: b })} onSubmit={bewaarEdit} vast={bezig} />
              <div className="flex gap-2">
                <button type="button" data-testid="reactie-edit-opslaan" onClick={bewaarEdit} disabled={bezig || editOngeldig}
                  className="px-3 py-1.5 min-h-[44px] sm:min-h-0 rounded-md bg-zinc-950 text-white text-xs font-semibold hover:bg-zinc-800 disabled:opacity-40">Opslaan</button>
                <button type="button" data-testid="reactie-edit-annuleren" onClick={() => setEdit(null)} disabled={bezig}
                  className="px-3 py-1.5 min-h-[44px] sm:min-h-0 rounded-md border border-zinc-300 bg-white text-zinc-800 text-xs font-semibold hover:border-zinc-500">Annuleren</button>
              </div>
            </li>
          ) : <Regel key={r.id} r={r} me={me} leesalleen={leesalleen} onEdit={(x) => setEdit({ id: x.id, tekst: x.tekst, besluit: !!x.besluit })} onWis={setWis} />))}
        </ul>
      )}
      {leesalleen ? <p data-testid="reacties-leesalleen" className="text-xs text-zinc-500">Deze afspraak is verwijderd. Zet hem terug om te reageren.</p> : (
        <div className="space-y-2">
          <Invoer idp="reactie-nieuw" label="Nieuwe reactie" waarde={tekst} onTekst={setTekst} besluit={besluit} onBesluit={setBesluit} onSubmit={plaats} placeholder="Schrijf een reactie…" vast={bezig} />
          <button type="button" data-testid="reactie-plaatsen" onClick={plaats} disabled={leeg || bezig}
            className="px-3.5 py-2 min-h-[44px] sm:min-h-0 rounded-md bg-zinc-950 text-white text-xs font-semibold hover:bg-zinc-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            Plaatsen
          </button>
        </div>
      )}
      <AlertDialog open={!!wis} onOpenChange={(o) => { if (!o) setWis(null); }}>
        <AlertDialogContent data-testid="reactie-verwijder-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Reactie verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>Deze reactie wordt definitief verwijderd. Dit kan niet ongedaan worden gemaakt.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reactie-verwijder-annuleren">Annuleren</AlertDialogCancel>
            <AlertDialogAction data-testid="reactie-verwijder-bevestig" onClick={verwijder} className="bg-rose-600 hover:bg-rose-700">Verwijderen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
